import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { useCart } from '@/hooks/useCart';
import { getProductImage } from '@/lib/productMedia';
import { isDigitalProduct } from '@/lib/productType';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';
import { normalizeCurrency } from '@/lib/currency';

interface ProductItem {
  id: number;
  name: string;
  description: string | null;
  price: number;
  discount_enabled: boolean;
  discount_price: number | null;
  media: unknown;
  category_id: number | null;
  status: string;
  created_at: string;
  stock: number | null;
  currency: string | null;
  product_type: string | null;
  digital_file_url: string | null;
}

interface ProductsTagProps {
  limit?: number;
  /** Restreint la grille à certaines catégories (utilisé par {{restauration}}). */
  categoryIds?: number[];
}

export default function ProductsTag({ limit, categoryIds }: ProductsTagProps) {
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { tenant, isTenant } = useTenant();
  const { addToCart } = useCart();

  useEffect(() => {
    setLoading(true);
    let query = supabase
      .from('product_items')
      .select('*')
      .eq('status', 'active');

    if (isTenant && tenant) {
      query = query.eq('idcommerce', tenant.id);
    }

    if (categoryIds && categoryIds.length > 0) {
      query = query.in('category_id', categoryIds);
    }

    if (limit) query = query.limit(limit);

    query
      .order('created_at', { ascending: false })
      .then(({ data, error: fetchErr }) => {
        if (fetchErr) setError(fetchErr.message);
        else setProducts((data as ProductItem[]) || []);
      })
      .catch(() => setError('Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [isTenant, tenant, limit, categoryIds]);

  const monaie = normalizeCurrency(tenant?.monaie || 'MAD');
  const { display, original } = useDisplayCurrency(monaie);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-10">
        <i className="ri-error-warning-line text-3xl text-red-400 mb-2"></i>
        <p className="text-sm text-foreground-500">{error}</p>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10">
        <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mb-3">
          <i className="ri-shopping-bag-line text-xl text-foreground-400"></i>
        </div>
        <p className="text-sm text-foreground-500">Aucun produit pour le moment</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
      {products.map((product) => {
        const imgSrc = getProductImage(product.media);
        const price = Number(product.price || 0);
        const promo = product.discount_enabled && product.discount_price != null;
        const effective = promo ? Number(product.discount_price) : price;
        const digital = isDigitalProduct(product);
        const outOfStock = !digital && product.stock != null && product.stock <= 0;

        return (
          <div
            key={product.id}
            className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-background-300/60 transition-all duration-200 group flex flex-col"
            data-product-shop="true"
          >
            <Link to={`/product/${product.id}`} className="block aspect-[16/10] bg-background-100 overflow-hidden relative no-underline">
              {imgSrc ? (
                <img
                  src={imgSrc}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <i className={`${digital ? 'ri-file-download-line' : 'ri-shopping-bag-line'} text-3xl text-foreground-300`}></i>
                </div>
              )}
              <div className="absolute top-2 left-2 flex flex-col items-start gap-1">
                {digital && (
                  <span className="px-2 py-0.5 bg-secondary-500 text-background-50 rounded-full text-xs font-semibold flex items-center gap-1">
                    <i className="ri-download-cloud-2-line"></i>Digital
                  </span>
                )}
                {promo && (
                  <span className="px-2 py-0.5 bg-accent-500 text-background-50 rounded-full text-xs font-semibold">Promo</span>
                )}
              </div>
            </Link>
            <div className="p-4 flex flex-col flex-1">
              <Link to={`/product/${product.id}`} className="no-underline">
                <h3 className="text-sm font-semibold text-foreground-900 mb-1 line-clamp-2 hover:text-primary-600 transition-colors">
                  {product.name}
                </h3>
              </Link>
              <div className="flex items-center gap-2">
                {promo && product.discount_price != null ? (
                  <>
                    <span className="text-base font-bold text-primary-600">
                      {display(Number(product.discount_price), monaie)}
                    </span>
                    <span className="text-xs text-foreground-400 line-through">
                      {display(price, monaie)}
                    </span>
                  </>
                ) : (
                  <span className="text-base font-bold text-primary-600">
                    {price ? display(price, monaie) : ''}
                  </span>
                )}
              </div>
              {original(effective, monaie) && (
                <span className="block mt-0.5 text-[11px] text-foreground-400">
                  {original(effective, monaie)}
                </span>
              )}

              <button
                type="button"
                onClick={() =>
                  addToCart({
                    id: product.id,
                    name: product.name,
                    price: effective,
                    original_price: promo ? price : null,
                    media: imgSrc || '',
                    stock: digital ? 9999 : (product.stock ?? 9999),
                    product_type: product.product_type || 'physical',
                    digital_file_url: product.digital_file_url,
                  })
                }
                disabled={outOfStock}
                className={`mt-3 w-full flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  outOfStock
                    ? 'bg-background-100 text-foreground-400 cursor-not-allowed'
                    : 'bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer'
                }`}
              >
                <i className={digital ? 'ri-download-2-line' : 'ri-shopping-cart-line'}></i>
                {outOfStock ? 'Rupture de stock' : digital ? 'Ajouter (digital)' : 'Ajouter au panier'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}